import { useEffect, useRef, useState } from "react";

const PAD = { l: 8, r: 64, t: 10, b: 20 };
const STRIP_H = 22;
const REGIME_COLOR = { trending_up: "#0ecb81", trending_down: "#f6465d", ranging: "#3b82f6", transitional: "#5e6673" };
const VOL_COLOR = { high: "#fcd535", normal: "#2b3139", low: "#1e2329" };

export function CandleChart({ candles = [], height = 380, showEma = true, showBB = true, markers = null }) {
  const ref = useRef(null);
  const [width, setWidth] = useState(800);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver((entries) => setWidth(Math.max(200, entries[0].contentRect.width)));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  const data = markers ? candles.slice(-Math.min(candles.length, Math.floor((width - PAD.l - PAD.r) / 4))) : candles.slice(-Math.min(candles.length, Math.floor((width - PAD.l - PAD.r) / 6)));
  const n = data.length;
  if (!n) return <div ref={ref} className="h-[380px] flex items-center justify-center text-[#5e6673] text-xs">Loading chart…</div>;

  const mLevels = markers ? [markers.entry_price, markers.stop_loss, ...(markers.take_profit || []), markers.exit_price].filter((v) => v != null) : [];

  const volH = 60;
  const hasRegime = data.some((d) => d.regime);
  const stripH = hasRegime ? STRIP_H : 0;
  const chartH = height - PAD.t - PAD.b - volH - stripH;
  const volBase = height - PAD.b - stripH;
  const plotW = width - PAD.l - PAD.r;
  const cw = plotW / n;
  const lows = data.map((d) => Math.min(d.low, showBB && d.bb_lower ? d.bb_lower : d.low)).concat(mLevels);
  const highs = data.map((d) => Math.max(d.high, showBB && d.bb_upper ? d.bb_upper : d.high)).concat(mLevels);
  const min = Math.min(...lows), max = Math.max(...highs);
  const range = max - min || 1;
  const y = (p) => PAD.t + (max - p) / range * chartH;
  const x = (i) => PAD.l + i * cw + cw / 2;
  const maxVol = Math.max(...data.map((d) => d.volume)) || 1;
  const line = (key) => data.map((d, i) => (d[key] == null ? null : `${x(i).toFixed(1)},${y(d[key]).toFixed(1)}`)).filter(Boolean).join(" ");
  const ticks = Array.from({ length: 6 }, (_, i) => min + range * i / 5);
  const last = data[n - 1];
  const h = hover != null ? data[hover] : last;
  const fmt = (v) => (v >= 1000 ? v.toFixed(1) : v >= 1 ? v.toFixed(3) : v.toFixed(5));

  const onMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const i = Math.floor((e.clientX - rect.left - PAD.l) / cw);
    setHover(i >= 0 && i < n ? i : null);
  };

  return (
    <div ref={ref} className="relative w-full select-none" data-testid="candlestick-chart-container" onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
      <div className="absolute left-2 top-1 flex gap-3 mono text-[10px] text-[#848e9c] pointer-events-none z-10">
        <span>O <span className="text-white">{fmt(h.open)}</span></span><span>H <span className="text-white">{fmt(h.high)}</span></span>
        <span>L <span className="text-white">{fmt(h.low)}</span></span><span>C <span className={h.close >= h.open ? "up" : "down"}>{fmt(h.close)}</span></span>
        {showEma && <span className="text-[#fcd535]">EMA20 {h.ema20 ? fmt(h.ema20) : "—"}</span>}
        {showEma && <span className="text-[#3b82f6]">EMA50 {h.ema50 ? fmt(h.ema50) : "—"}</span>}
        <span className="hidden sm:inline">RSI <span className="text-white">{h.rsi?.toFixed(1) ?? "—"}</span></span>
      </div>
      <svg width={width} height={height} className="block">
        {ticks.map((t) => (
          <g key={t}>
            <line x1={PAD.l} x2={width - PAD.r} y1={y(t)} y2={y(t)} stroke="#1e2329" strokeWidth="1" />
            <text x={width - PAD.r + 6} y={y(t) + 3} fill="#5e6673" fontSize="10" fontFamily="IBM Plex Mono">{fmt(t)}</text>
          </g>
        ))}
        {showBB && <polyline points={line("bb_upper")} fill="none" stroke="#5e6673" strokeWidth="1" strokeDasharray="3 3" />}
        {showBB && <polyline points={line("bb_lower")} fill="none" stroke="#5e6673" strokeWidth="1" strokeDasharray="3 3" />}
        {showEma && <polyline points={line("ema20")} fill="none" stroke="#fcd535" strokeWidth="1.2" />}
        {showEma && <polyline points={line("ema50")} fill="none" stroke="#3b82f6" strokeWidth="1.2" />}
        {data.map((d, i) => {
          const up = d.close >= d.open;
          const c = up ? "#0ecb81" : "#f6465d";
          const top = y(Math.max(d.open, d.close)), bot = y(Math.min(d.open, d.close));
          const bw = Math.max(1, cw * 0.62);
          return (
            <g key={d.timestamp}>
              <line x1={x(i)} x2={x(i)} y1={y(d.high)} y2={y(d.low)} stroke={c} strokeWidth="1" />
              <rect x={x(i) - bw / 2} y={top} width={bw} height={Math.max(1, bot - top)} fill={c} />
              <rect x={x(i) - bw / 2} y={volBase - (d.volume / maxVol) * volH} width={bw} height={(d.volume / maxVol) * volH} fill={c} opacity="0.35" />
              {hasRegime && <rect x={x(i) - cw / 2} y={volBase + 4} width={cw + 0.5} height={7} fill={REGIME_COLOR[d.regime] || "#2b3139"} opacity="0.9" data-regime={d.regime} />}
              {hasRegime && <rect x={x(i) - cw / 2} y={volBase + 13} width={cw + 0.5} height={5} fill={VOL_COLOR[d.vol_regime] || "#2b3139"} opacity={d.vol_regime === "high" ? 0.95 : 0.8} />}
            </g>
          );
        })}
        <line x1={PAD.l} x2={width - PAD.r} y1={y(last.close)} y2={y(last.close)} stroke={last.close >= last.open ? "#0ecb81" : "#f6465d"} strokeWidth="1" strokeDasharray="2 2" />
        <rect x={width - PAD.r + 2} y={y(last.close) - 8} width={PAD.r - 4} height={16} fill={last.close >= last.open ? "#0ecb81" : "#f6465d"} rx="2" />
        <text x={width - PAD.r + 6} y={y(last.close) + 4} fill="#0b0e11" fontSize="10" fontWeight="600" fontFamily="IBM Plex Mono">{fmt(last.close)}</text>
        {hover != null && <line x1={x(hover)} x2={x(hover)} y1={PAD.t} y2={height - PAD.b} stroke="#848e9c" strokeWidth="1" strokeDasharray="2 2" />}
        {markers && (() => {
          const idxOf = (ts) => { if (ts == null) return null; let best = null; data.forEach((d, i) => { if (d.timestamp <= ts) best = i; }); return best; };
          const ei = idxOf(markers.entry_ts), xi = idxOf(markers.exit_ts);
          const lvl = (v, color, label) => v == null ? null : (
            <g key={label}><line x1={PAD.l} x2={width - PAD.r} y1={y(v)} y2={y(v)} stroke={color} strokeWidth="1" strokeDasharray="4 3" />
              <text x={PAD.l + 4} y={y(v) - 3} fill={color} fontSize="9" fontFamily="IBM Plex Mono">{label} {fmt(v)}</text></g>);
          return (
            <g data-testid="replay-markers">
              {ei != null && <rect x={x(ei) - cw / 2} y={PAD.t} width={xi != null ? Math.max(cw, x(xi) - x(ei) + cw) : Math.max(cw, plotW - (x(ei) - PAD.l))} height={chartH} fill={markers.side === "LONG" ? "rgba(14,203,129,0.06)" : "rgba(246,70,93,0.06)"} />}
              {lvl(markers.entry_price, "#fcd535", "ENTRY")}{lvl(markers.stop_loss, "#f6465d", "STOP")}
              {(markers.take_profit || []).map((tp, i) => lvl(tp, "#0ecb81", `TP${i + 1}`))}
              {ei != null && <><line x1={x(ei)} x2={x(ei)} y1={PAD.t} y2={height - PAD.b} stroke="#fcd535" strokeWidth="1.5" /><circle cx={x(ei)} cy={y(markers.entry_price)} r="4" fill="#fcd535" /></>}
              {xi != null && markers.exit_price != null && <><line x1={x(xi)} x2={x(xi)} y1={PAD.t} y2={height - PAD.b} stroke="#eaecef" strokeWidth="1" strokeDasharray="3 3" /><circle cx={x(xi)} cy={y(markers.exit_price)} r="4" fill="#eaecef" /></>}
            </g>
          );
        })()}
        {hasRegime && <text x={width - PAD.r + 6} y={volBase + 11} fill="#5e6673" fontSize="9" fontFamily="IBM Plex Mono">REGIME</text>}
        {hasRegime && <text x={width - PAD.r + 6} y={volBase + 19} fill="#5e6673" fontSize="9" fontFamily="IBM Plex Mono">VOLAT.</text>}
        {[0, Math.floor(n / 2), n - 1].map((i) => (
          <text key={i} x={x(i)} y={height - 5} fill="#5e6673" fontSize="10" textAnchor="middle" fontFamily="IBM Plex Mono">
            {new Date(data[i].timestamp).toLocaleString("en-GB", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false })}
          </text>
        ))}
      </svg>
      {hasRegime && (
        <div className="flex flex-wrap items-center gap-3 px-2 pt-1 text-[10px] mono text-[#848e9c]" data-testid="regime-legend">
          <span className="text-[#5e6673]">REGIME</span>
          {[["trending_up", "trend ↑"], ["trending_down", "trend ↓"], ["ranging", "ranging"], ["transitional", "transition"]].map(([k, l]) => <span key={k} className="flex items-center gap-1"><span className="w-2.5 h-2 inline-block rounded-[1px]" style={{ background: REGIME_COLOR[k] }} />{l}</span>)}
          <span className="text-[#5e6673] ml-2">VOLATILITY</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2 inline-block rounded-[1px]" style={{ background: VOL_COLOR.high }} />high</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2 inline-block rounded-[1px] border border-[#3c4552]" style={{ background: VOL_COLOR.normal }} />normal/low</span>
          <span className="ml-auto text-[#eaecef]" data-testid="regime-hover">{h.regime ? `${h.regime.replace("_", " ")} · ${h.vol_regime} vol` : ""}</span>
        </div>
      )}
    </div>
  );
}
